import { useState } from "react"
import Icon from "../../components/Icon"
import PrepayModal from "./PrepayModal"
import { formatMoney } from "../../utils/format"

// Says where one company's credit came from: money the customer chose to
// prepay, extra they paid on bills that were already settled, or both.
const describeCreditOrigin = (issuer = {}) => {
  const prepaid = Number(issuer.prepaidAmount || 0)
  const overpaid = Number(issuer.overpaidAmount || 0)
  if (prepaid > 0 && overpaid > 0) {
    return `Prepaid ${formatMoney(prepaid)} · Overpaid ${formatMoney(overpaid)}`
  }
  if (overpaid > 0) return "From bills you overpaid"
  return "Prepaid"
}

// Credit the customer holds with billing companies, and the button that
// creates more of it by prepaying. Each row is one company - or one service of
// a company - and what it still holds for this person.
function PrepaidCreditPanel({ prepaymentCredit, onPrepaid }) {
  const [showPrepay, setShowPrepay] = useState(false)

  const issuers = Array.isArray(prepaymentCredit?.issuers) ? prepaymentCredit.issuers : []
  const totalRemaining = Number(prepaymentCredit?.totalRemaining || 0)

  return (
    <section className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="arrow-up-right-circle" size="0.9rem" className="text-emerald-600" />
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Credit with companies</p>
          </div>
          <p className="mt-1 text-xl font-black tracking-tight text-slate-900">
            {formatMoney(totalRemaining)}
          </p>
          {/* Credit is already part of Account credit above. Saying so stops
              the two tiles reading as separate pots of money. */}
          <p className="mt-0.5 text-[11px] text-slate-500">
            Pays that company's next bills before your wallet · included in account credit
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowPrepay(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-emerald-700"
        >
          <Icon name="plus-lg" size="0.75rem" />
          Prepay
        </button>
      </div>

      {issuers.length > 0 && (
        <>
          <ul className="mt-2.5 divide-y divide-slate-100 border-t border-slate-100">
            {issuers.map((issuer) => (
              <li key={issuer.key || issuer.providerId} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-700">
                    <span className="truncate">{issuer.providerName || "Billing company"}</span>
                    {issuer.accountLabel && (
                      <span className="ml-1.5 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        {issuer.accountLabel}
                      </span>
                    )}
                  </span>
                  <span className="block text-[11px] text-slate-500">{describeCreditOrigin(issuer)}</span>
                </span>
                <span className="shrink-0 text-sm font-black tabular-nums text-emerald-700">
                  {formatMoney(issuer.remainingAmount)}
                </span>
              </li>
            ))}
          </ul>
          {/* Credit belongs to the company that received the money. Telling the
              tenant now means moving out never comes as a surprise. */}
          <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-800">
            If you move out, unused credit stays with that company. Ask them to use it on your last bills first.
          </p>
        </>
      )}

      {showPrepay && (
        <PrepayModal
          onDone={onPrepaid}
          onClose={() => setShowPrepay(false)}
        />
      )}
    </section>
  )
}

export default PrepaidCreditPanel
